import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import cs from 'classnames';
import { useRecoilValue } from 'recoil';

import { useClinicalNoteContext } from 'lib/context/ClinicalNoteContext/ClinicalNoteContext';
import { ia, iaRa } from 'lib/helpers/utility';

import Checkbox from 'components/shared/Checkbox/Checkbox';
import Loader from 'components/shared/Loader/Loader';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Switch from 'components/shared/Switch/Switch';
import Textarea from 'components/shared/Textarea/Textarea';
import {
  clinicalNote as clinicalNoteState,
  intelligenceAiState,
  userState
} from 'components/state';

import EnhancementStatus from '../CurrentNote/components/EnhancementStatus';
import { enhanceNoteLoadingState } from '../MyScribeAI/state';

const NarrativeForm = ({
  restData = { customFormId: undefined },
  setCurrentForm = () => null,
  narrativeOptions = [],
  syncNarrative,
  setSyncNarrative = () => null,
  onChange = () => null,
  narrativeClassName = null,
  textAreaClassName = null,
  isLoading = false
}) => {
  const { components, isNoteCanceled } = useClinicalNoteContext();
  const clinicalNote = useRecoilValue(clinicalNoteState);
  const intelligenceAi = useRecoilValue(intelligenceAiState);
  const enhanceNoteLoading = useRecoilValue(enhanceNoteLoadingState);
  const user = useRecoilValue(userState);

  const [confirmationModal, setConfirmationModal] = useState({
    show: false,
    doNotShowAgainCheckbox: false
  });
  const { formName: formNameParam } = useParams();

  let formName = restData?.formName ?? formNameParam ?? Object.values(components)?.[0]?.path;
  if (formName === 'medications') formName = 'medicationHistory';

  const clearNarrative = () => setCurrentForm((prevState) => ({ ...prevState, narrative: null }));

  const shouldHideConfirmationModalNextTime = () => {
    const narrative_sync_checkbox = localStorage.getItem('narrative_sync_checkbox');

    const parsedNarrativeSyncCheckbox =
      typeof narrative_sync_checkbox === 'string' ? JSON.parse(narrative_sync_checkbox) : {};

    return (
      parsedNarrativeSyncCheckbox?.doNotShowConfirmModal &&
      parsedNarrativeSyncCheckbox?.userId === user?.id
    );
  };

  const switchSyncNarrative = () => {
    const hasUserCheckedDoNotShowAgain = shouldHideConfirmationModalNextTime();

    if (!syncNarrative && !hasUserCheckedDoNotShowAgain) {
      setConfirmationModal((prevState) => ({ ...prevState, show: true }));
      return;
    }

    setSyncNarrative((prev) => !prev);
  };

  const handleContinue = () => {
    setSyncNarrative(true);

    if (confirmationModal?.doNotShowAgainCheckbox) onCheckboxChange();

    setConfirmationModal((prevState) => ({
      ...prevState,
      show: false,
      doNotShowAgainCheckbox: false
    }));
  };

  useEffect(() => {
    if (intelligenceAi?.path?.includes?.(formName) && restData?.value !== intelligenceAi.response) {
      onChange(intelligenceAi.response);
    }
  }, [intelligenceAi]);

  const newOptions = [
    {
      icon: 'refresh-linear',
      title: syncNarrative ? 'Unsync' : 'Sync',
      rightIcon: <Switch onChange={() => null} checked={syncNarrative} />,
      onClick: switchSyncNarrative,
      group: 'actions'
    },
    {
      icon: 'broom-mono',
      title: 'Clear',
      onClick: clearNarrative,
      group: 'actions'
    }
  ];
  newOptions.push(...iaRa(narrativeOptions));

  const onCheckboxChange = () => {
    const objectToInsert = JSON.stringify({
      doNotShowConfirmModal: event,
      userId: user?.id
    });

    localStorage.setItem('narrative_sync_checkbox', objectToInsert);
  };

  const handleQuillChange = (content, delta, source) => {
    if (ia(delta?.ops) && source === 'user') {
      onChange(content);
      return;
    }
    if (!delta && !source) {
      onChange(content);
      return;
    }
  };

  const NarrativeLabelWithSwitch = () => {
    return (
      <div className="flex items-center gap-1.5 transition-all hover:bg-primary-50">
        <EnhancementStatus type={formName}>
          <p>Narrative</p>
        </EnhancementStatus>
        {isLoading && <Loader outlined={true} className="bg-primary-500 text-white" />}
      </div>
    );
  };

  return (
    <div className={cs('!mt-3', narrativeClassName)}>
      <Textarea
        {...restData}
        isEditor
        label={<NarrativeLabelWithSwitch />}
        placeholder="Type narrative"
        transcribing
        onChange={handleQuillChange}
        options={newOptions}
        optionsGroupBy="group"
        disabled={clinicalNote?.locked || enhanceNoteLoading.includes(formName) || isNoteCanceled}
        className={cs('min-h-[200px]', textAreaClassName)}
      />

      {confirmationModal?.show && (
        <Confirm
          title="Sync Narrative"
          variant="danger"
          icon="warning-2-mono"
          message={
            <div>
              <p>
                Enabling Sync Narrative will automatically generate the narrative as you interact
                with the form,{' '}
                <i className="text-danger-600">
                  causing any manually entered text in the narrative box to be lost.
                </i>
              </p>
              <div className="!mt-3">
                <Checkbox
                  id="confirm-narrative-checkbox"
                  label={<p className="text-sm">Do not show this message again</p>}
                  color="blue"
                  isChecked={confirmationModal?.doNotShowAgainCheckbox}
                  onChange={(event) =>
                    setConfirmationModal((prevState) => ({
                      ...prevState,
                      doNotShowAgainCheckbox: event.target.checked
                    }))
                  }
                />
              </div>
            </div>
          }
          handleOpen={confirmationModal?.show}
          handleClose={() => setConfirmationModal((prevState) => ({ ...prevState, show: false }))}
          footerActions={{
            primary: {
              text: 'Continue',
              onClick: handleContinue,
              color: 'danger'
            }
          }}
        />
      )}
    </div>
  );
};

export default NarrativeForm;
